// Чистые view-хелперы экрана разбора (шапка, шелл, состояние ошибки, метки чипов).
// Вынесены из game.ts, чтобы держать экран под лимитом 400 строк. Без состояния —
// только строки/DOM по модели.
import { Q_GLYPH, type MoveQuality } from '@ext/core/classify';
import { t } from '../i18n';
import type { MoveVM, ReviewModel } from '../core/review-model';

export function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] || c);
}

export function qClass(q: MoveQuality | null): string {
  if (q === 'blunder' || q === 'miss') return 'blunder';
  if (q === 'mistake') return 'mistake';
  if (q === 'inaccuracy') return 'warn';
  if (q === 'brilliant' || q === 'great') return 'good';
  return '';
}

export function fmtEval(cp: number | null): string {
  if (cp == null) return '';
  if (Math.abs(cp) >= 2000) return cp > 0 ? '+#' : '−#';
  const p = cp / 100;
  const sign = p > 0 ? '+' : p < 0 ? '−' : '';
  return `${sign}${Math.abs(p).toFixed(1)}`;
}

export function chipLabel(mv: MoveVM): string {
  const num = mv.white ? `${mv.moveNo}.` : `${mv.moveNo}…`;
  const glyph = mv.quality && qClass(mv.quality) ? `${Q_GLYPH[mv.quality]} ` : '';
  return `${num} ${glyph}${esc(mv.san)}`;
}

export function headHtml(m: ReviewModel): string {
  const wl = m.whiteElo ? ` <span class="mono tiny">${esc(m.whiteElo)}</span>` : '';
  const bl = m.blackElo ? ` <span class="mono tiny">${esc(m.blackElo)}</span>` : '';
  const eyebrow = `${esc(m.white)}${wl} <span class="muted">${t('game.vs')}</span> ${esc(m.black)}${bl}`;
  const title = [m.result, m.speed].filter(Boolean).map(esc).join(' · ') || t('game.title');
  const acc =
    m.engineReady && m.accuracy != null
      ? `<div class="accuracy" title="${t('game.accuracy')}">${Math.round(m.accuracy)}%</div>`
      : '';
  const q = (cls: string, glyph: string, n: number, word: string) =>
    `<span class="badge ${cls}"><span class="quality-${cls}">${glyph}</span> ${n} ${word}</span>`;
  const counters = m.engineReady
    ? `<div class="quality-row">
        ${q('blunder', '??', m.counts.blunder, t('game.blunders'))}
        ${q('mistake', '?', m.counts.mistake, t('game.mistakes'))}
        ${q('warn', '?!', m.counts.inaccuracy, t('game.inaccuracies'))}
      </div>`
    : '';
  return `
    <div class="row gap12">
      <div class="grow"><div class="eyebrow" style="text-transform:none;letter-spacing:.02em">${eyebrow}</div>
        <h1 style="font-size:24px;margin-top:4px">${title}</h1></div>
      ${acc}
    </div>
    ${counters}`;
}

export function shellHtml(m: ReviewModel, willAnalyze: boolean): string {
  const progress = willAnalyze
    ? `<section class="analysis-progress hidden" data-progress>
        <div class="row between"><strong class="tiny" data-progress-label>${t('game.analyzing', { done: 0, total: 0 })}</strong></div>
        <div class="progress" style="--value:0%" data-progress-bar><i></i></div>
      </section>`
    : '';
  return `
    <header class="topbar">
      <span class="back" data-back>${t('top.back')}</span>
      <span class="top-title"><span class="top-logo" aria-hidden="true"></span>${t('game.title')}</span>
      <span class="top-action">Stockfish</span>
    </header>
    <section class="review-head" data-head>${headHtml(m)}</section>
    <div class="shared-cta hidden" data-shared-cta style="margin:12px 0">
      <button type="button" class="inline-cta" data-analyze-own>${t('game.analyze_own')}</button>
    </div>
    <div class="share-row hidden" data-share-row>
      <button type="button" class="inline-cta share-btn" data-share>${t('game.share_btn')}</button>
    </div>
    <div class="pgn-review-actions hidden" data-pgn-actions>
      <button type="button" class="ghost-btn" data-pgn-save>☆ ${t('my.add')}</button>
      <button type="button" class="inline-cta" data-pgn-share>↗ ${t('my.share')}</button>
    </div>
    <section class="review-board">
      <div class="eval-bar" data-eval><span class="black"></span><span class="white"></span></div>
      <div class="board-stage" data-board></div>
    </section>
    <div class="row best-toggle hidden">
      <label class="switch"><input type="checkbox" data-best-toggle checked>${t('game.show_best')}</label>
    </div>
    ${progress}
    <nav class="move-strip" data-moves aria-label="${t('game.moves')}"></nav>
    <div data-nav></div>
    <section class="variation-bar" data-variation>
      <span class="badge good">${t('game.variation_badge')}</span>
      <span class="var-moves mono tiny" data-var-moves></span>
      <button class="nav-button" type="button" data-var-return>${t('game.variation_return')}</button>
    </section>
    <section class="card comment-card"><h2 data-card-title></h2><p style="margin-top:8px;white-space:pre-line" data-card-body></p>
      <a class="inline-cta train-link hidden" data-train-link>${t('game.train_position')}</a></section>
    <section class="card engine-lines-card">
      <div class="eyebrow">${t('game.top_lines')}</div>
      <div class="engine-lines" data-lines></div>
    </section>
    <div data-play-host></div>
    <section style="margin-top:14px"><div class="eyebrow">${t('game.graph_title')}</div>
      <div class="eval-graph" data-graph role="button" aria-label="${t('game.graph_title')}">
        <div class="graph-cursor" data-cursor></div></div></section>
    <section class="card chrome-cta">
      <h2>${t('game.cta_title')}</h2>
      <a class="inline-cta" data-ext>${t('game.cta_button')}</a>
    </section>
    <div class="toast hidden" data-toast></div>`;
}

export function errorState(root: HTMLElement, backHash: string): void {
  root.className = 'app v2';
  root.innerHTML = `
    <header class="topbar"><span class="back" data-back>${t('top.back')}</span>
      <span class="top-title">${t('game.title')}</span><span class="top-action"></span></header>
    <section class="card state-card"><span class="icon">\u{1f61f}</span>
      <p class="muted">${t('game.err_load')}</p>
      <button type="button" class="retry-btn" data-retry>${t('games.retry')}</button></section>`;
  const back = (): void => {
    location.hash = backHash;
  };
  root.querySelector<HTMLElement>('[data-back]')?.addEventListener('click', back);
  root.querySelector<HTMLElement>('[data-retry]')?.addEventListener('click', back);
}
